import { useQuery } from '@tanstack/react-query';
import { locale, useAppContext } from '../../contexts/AppContext';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import type { AxiosError } from 'axios';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WidgetsIcon from '@mui/icons-material/Widgets';
import { AnalysisResult, MitigationSolution, Server } from '../../services/Server';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CheckIcon from '@mui/icons-material/Check';
import CompareCleaningSchedules from './CompareCleaningSchedules';
import CompareProductionLosses from './CompareProductionLosses';
import { handleError } from '../../utils/Error';
import toast from 'react-hot-toast';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PieChart from '../common/PieChart';
import SoilingRatioChart from './SoilingRatioChart';
import ProductionChart from './ProductionChart';
import HeatMapChart from './HeatMapChart';
import SMTCard from '../common/SMTCard';
import { InfoAlert } from '../common/InfoAlert';
import CachedResultBadge from '../common/CachedResultBadge';
import { getSolutionDisplayName, isEmpty } from '../../utils/utils';
import FinancialComparison from './FinancialComparison';
import DownloadCashflowButton from './DownloadCashflowButton';
import { useFormData } from '../../hooks/useFormValues';
import { getImageKeyForPackage } from '../../utils/utils';

type Props = {
  projectId: string | undefined;
  setResults: Dispatch<SetStateAction<Record<string, unknown>>>;
};

const AppSoilingMitigationStrategy = ({ projectId, setResults }: Props) => {
  const formData = useFormData();
  const { setModified } = useAppContext();
  const { postMitigationStrategyAPI, getCleaningPackagesAPI, registerProgressCallback, unregisterProgressCallback } = Server();
  const [selectedSolutions, setSelectedSolutions] = useState<string[]>([]);
  const [chosenSolution, setChosenSolution] = useState(formData?.results?.solution);
  const [numberOfCleanings, setNumberOfCleanings] = useState(0);
  const [numberOfDevices, setNumberOfDevices] = useState(0);
  const [selectedSolution, setSelectedSolution] = useState<MitigationSolution | null>(null);
  const [copiedText, setCopiedText] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [progressMessage, setProgressMessage] = useState<string>('');
  const [cached, setCached] = useState<boolean>(false);

  useEffect(() => {
    if (selectedSolutions.length === 1) {
      setNumberOfCleanings(data?.package_solution_groups?.[selectedSolutions?.[0]]?.optimal_cleanings);
      setNumberOfDevices(data?.package_solution_groups?.[selectedSolutions?.[0]]?.optimal_devices);
    }
  }, [selectedSolutions]);

  useEffect(() => {
    if (selectedSolutions.length === 1) {
      setSelectedSolution(
        data?.package_solution_groups?.[selectedSolutions?.[0]]?.solutions?.filter(
          (solution: MitigationSolution) => solution.num_cleanings === numberOfCleanings && solution.num_devices === numberOfDevices,
        )?.[0] || null,
      );
    }
  }, [selectedSolutions, numberOfCleanings, numberOfDevices]);

  useEffect(() => {
    registerProgressCallback('mitigation-strategy', (progressMessage: string) => {
      setProgressMessage(progressMessage);
    });

    return () => {
      unregisterProgressCallback('mitigation-strategy');
    };
  }, [registerProgressCallback, unregisterProgressCallback]);

  const { data: cleaningPackages = {} } = useQuery({
    queryKey: ['cleaning_packages'],
    queryFn: () => getCleaningPackagesAPI(),
  });

  const getMitigationStrategyQueryKey = useCallback(() => {
    if (!formData) return ['cleaning-compare'];

    const excludeFields = ['last_updated'];

    const filteredFormData = Object.keys(formData).reduce(
      (acc, key) => {
        if (!excludeFields.includes(key)) {
          acc[key] = formData[key];
        }
        return acc;
      },
      {} as Record<string, unknown>,
    );

    return ['cleaning-compare', JSON.stringify(filteredFormData)];
  }, [formData]);

  const hasCleaningPackageSelected = Object.keys(formData?.cleaning_package || {}).some((key) => formData.cleaning_package[key] === true);

  const {
    data: data = {} as AnalysisResult,
    isLoading: isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: getMitigationStrategyQueryKey(),
    queryFn: () => {
      setProgressMessage('');
      setCached(false);
      return postMitigationStrategyAPI(
        {
          project_id: projectId,
        },
        {},
        setCached,
      );
    },
    enabled: hasCleaningPackageSelected,
    gcTime: 0,
  });

  useEffect(() => {
    if (isError) {
      const detail = (error as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail.forEach((elem: { msg: string; loc: string[] }) => toast.error(`${elem.msg}: ${elem.loc.join(' - ')}`));
      } else {
        handleError(error);
      }
    }
  }, [isError, error]);

  const handleTextCopy = (text: string[]) => {
    copyText(text);
    setCopiedText(true);
    setTimeout(() => {
      setCopiedText(false);
    }, 1500);
  };

  const copyText = (text: string[]) => {
    let txt = 'Month\tSoiling Ratio\tSoiling Level\n';
    text.forEach((t: string, index: number) => {
      const d = new Date(Date.parse(t));
      const ts = d.toLocaleString(locale, { month: 'long' });
      txt +=
        ts +
        '\t' +
        Math.round((selectedSolution?.data?.monthly?.values?.soiling_ratio?.[index] ?? 0) * 10000) / 10000 +
        '\t' +
        Math.round((1 - (selectedSolution?.data?.monthly?.values?.soiling_ratio?.[index] ?? 0)) * 10000) / 10000 +
        '\n';
    });

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(txt);
    } else {
      console.error('Clipboard API is not supported in this environment.');
    }
  };

  const handleFinancialTextCopy = (text: number[]) => {
    copyFinancialText(text);
    setCopiedText(true);
    setTimeout(() => {
      setCopiedText(false);
    }, 1500);
  };
  const copyFinancialText = (text: number[]) => {
    let txt = 'Year\tCleaning Gains (MU)\tCAPEX (MU)\tCAPEX (MU)\n';
    text.forEach((_t, index: number) => {
      txt +=
        index +
        '\t' +
        Math.round((selectedSolution?.cashflow?.cleaning_gains[index] ?? 0) * 100) / 100 +
        '\t' +
        Math.round((selectedSolution?.cashflow?.capex[index] ?? 0) * 100) / 100 +
        '\t' +
        Math.round((selectedSolution?.cashflow?.opex[index] ?? 0) * 100) / 100 +
        '\n';
    });
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(txt);
    } else {
      console.error('Clipboard API is not supported in this environment.');
    }
  };

  const handleCleaningsChange = (e: SelectChangeEvent<unknown>) => {
    setNumberOfCleanings(Number(e.target.value));
  };

  const handleDevicesChange = (e: SelectChangeEvent<unknown>) => {
    setNumberOfDevices(Number(e.target.value));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    let val: string | number | boolean = value;
    if (type === 'number') {
      val = Number(value);
    }
    if (type === 'checkbox') {
      val = checked;
    }
    let newSelectedSolutions = [];
    if (val) {
      newSelectedSolutions = [...selectedSolutions, name];
    } else {
      newSelectedSolutions = [...selectedSolutions.filter((k) => k !== name)];
    }
    setSelectedSolutions(newSelectedSolutions);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getOriginalKeyForImage = (packageName: string) => {
    return getImageKeyForPackage(packageName);
  };

  if (!hasCleaningPackageSelected) {
    return (
      <Box sx={{ mt: 0 }}>
        <Typography color="text.primary">Please select a cleaning package</Typography>
      </Box>
    );
  }

  const cleaningsOptions: number[] = Array.from(
    new Set((data?.package_solution_groups?.[selectedSolutions[0]]?.solutions || []).map((s) => s.num_cleanings)),
  ).sort((a, b) => a - b);
  const devicesOptions: number[] = Array.from(
    new Set((data?.package_solution_groups?.[selectedSolutions[0]]?.solutions || []).map((s) => s.num_devices)),
  ).sort((a, b) => a - b);

  return (
    <>
      <Box sx={{ justifyContent: 'center', pl: 1, pr: 1 }}>
        <Typography variant="h4" color="text.primary" sx={{ mb: 2 }}>
          Mitigation Strategy
        </Typography>
        <InfoAlert severity="info">
          This page provides a comparative evaluation of selected cleaning packages, highlighting the optimal schedule and best-performing
          one. It includes cost breakdown and net present value (NPV) analysis.
        </InfoAlert>
        {isLoading ? (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {progressMessage}
            </Typography>
          </Box>
        ) : null}
        {isLoading ? (
          <>
            <Skeleton width={'220px'} height={48} animation="wave" style={{ marginTop: '20px', marginBottom: '30px' }} />
            <Grid container spacing={2}>
              {Array.from(
                new Array(Object.keys(formData.cleaning_package).filter((item) => formData.cleaning_package[item] === true).length),
              ).map((_item, index) => (
                <Grid
                  key={index}
                  size={{
                    xs: 12,
                    md: Math.round(
                      12 / Object.keys(formData.cleaning_package).filter((item) => formData.cleaning_package[item] === true).length,
                    ),
                  }}
                >
                  <Skeleton variant="rectangular" height={230} width={'100%'} animation="wave" />
                </Grid>
              ))}
            </Grid>

            <Skeleton width={'150px'} height={24} animation="wave" style={{ marginTop: '10px' }} />
            <Skeleton variant="rectangular" height={350} animation="wave" style={{ marginTop: '10px' }} />
            <Skeleton variant="rectangular" height={350} animation="wave" style={{ marginTop: '20px' }} />
          </>
        ) : Object.keys(data).length > 0 ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
              <Typography variant="h5" color="text.primary">
                Overview of solutions
              </Typography>
              <CachedResultBadge cached={cached} />
            </Box>
            <Grid sx={{ mt: 2, mb: 2 }} container spacing={2} key={'cl_choice'}>
              {data?.package_solution_groups &&
                Object.keys(data?.package_solution_groups)
                  .sort()
                  .map((key) => {
                    return (
                      <Grid
                        key={key}
                        size={{
                          xs: 12,
                          md:
                            Math.round(12 / Object.keys(data?.package_solution_groups).length) > 2
                              ? Math.round(12 / Object.keys(data?.package_solution_groups).length)
                              : 2,
                        }}
                      >
                        <Card>
                          <CardMedia sx={{ height: 100 }} image={'/' + getOriginalKeyForImage(key) + '.png'} title={key} />
                          <CardContent>
                            <Typography gutterBottom variant="h5" component="div">
                              {getSolutionDisplayName(cleaningPackages, key)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="div">
                              <Chip
                                className={'solution-chip'}
                                icon={<CleaningServicesIcon />}
                                label={'Cleanings: ' + data.package_solution_groups[key].optimal_cleanings}
                              />{' '}
                              <Chip
                                className={'solution-chip'}
                                icon={<WidgetsIcon />}
                                label={'Devices: ' + data.package_solution_groups[key].optimal_devices}
                              />{' '}
                              <Chip
                                className={'solution-chip'}
                                icon={<AttachMoneyIcon />}
                                label={
                                  'ΔNPV: ' +
                                  Math.round(
                                    data.package_solution_groups[key].solutions?.[data.package_solution_groups[key].optimal_index]
                                      ?.net_present_value / 10000,
                                  ) /
                                    100 +
                                  ' M$'
                                }
                              />
                            </Typography>
                          </CardContent>
                          <CardActions>
                            <Checkbox
                              name={key}
                              // checked={item.checked}
                              checked={selectedSolutions.includes(key) || false}
                              inputProps={{ 'aria-label': 'controlled' }}
                              onChange={(e) => handleChange(e)}
                            />
                            <Button
                              variant={chosenSolution === key ? 'contained' : 'outlined'}
                              color={chosenSolution === key ? 'success' : 'info'}
                              startIcon={<CheckIcon />}
                              onClick={() => {
                                setChosenSolution(key);
                                setResults((prevResults) => {
                                  return {
                                    ...prevResults,
                                    solution: key,
                                    cleaning_frequency: data?.package_solution_groups[key].optimal_cleanings,
                                    yearly_soiling_ratio:
                                      Math.round(
                                        data?.package_solution_groups[key].solutions[data?.package_solution_groups[key].optimal_index]?.data
                                          .yearly.values.soiling_ratio[0] * 10000,
                                      ) / 100,
                                    average_soiling_loss:
                                      Math.round(
                                        (data?.package_solution_groups[key].solutions[data?.package_solution_groups[key].optimal_index]
                                          ?.data.yearly?.values?.production_losses[0] /
                                          12) *
                                          100,
                                      ) / 100,
                                    needed_devices: data?.package_solution_groups[key].optimal_devices,
                                    average_cleaning_gains:
                                      Math.round(
                                        (data?.package_solution_groups[key].solutions[data?.package_solution_groups[key].optimal_index]
                                          ?.data.yearly?.values?.cleaning_gains[0] /
                                          12) *
                                          100,
                                      ) / 100,
                                  };
                                });
                                setModified(true);
                              }}
                            >
                              Choose this solution
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
            </Grid>

            {selectedSolutions?.length === 0 ? <Typography color="text.primary">Select a solution above</Typography> : null}
            {selectedSolutions?.length === 1 ? (
              <>
                <Typography color="text.primary">1 solution selected.</Typography>
                <br />
                <Stack spacing={2}>
                  <Typography color="text.primary" variant="h4" sx={{ mb: 2 }}>
                    {getSolutionDisplayName(cleaningPackages, selectedSolutions?.[0])}
                  </Typography>
                  <HeatMapChart
                    name={'NPV Gain Heatmap for ' + getSolutionDisplayName(cleaningPackages, selectedSolutions?.[0])}
                    data={{ ...data.package_solution_groups[selectedSolutions[0]] }}
                  />
                  <Typography color="text.primary" variant="h5" sx={{ mb: 2 }}>
                    You can change the number of cleanings and devices below
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={4}>
                      <Select
                        fullWidth
                        name="nbr_of_cleanings"
                        value={cleaningsOptions.includes(numberOfCleanings) ? numberOfCleanings : ''}
                        onChange={(e) => handleCleaningsChange(e)}
                        sx={{ mb: 1 }}
                      >
                        {cleaningsOptions.map((val: number) => (
                          <MenuItem key={'c' + val} value={val}>
                            {val + ' cleaning' + (val !== 1 ? 's' : '')}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid size={4}>
                      <Select
                        fullWidth
                        name="nbr_of_devices"
                        value={devicesOptions.includes(numberOfDevices) ? numberOfDevices : ''}
                        onChange={(e) => handleDevicesChange(e)}
                      >
                        {devicesOptions.map((val: number) => (
                          <MenuItem key={'c' + val} value={val}>
                            {val + ' device' + (val !== 1 ? 's' : '')}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid size={4}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          height: '100%',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="h6" color="text.primary">
                          NPV Gain:{' '}
                          {selectedSolution?.net_present_value
                            ? Math.round(selectedSolution.net_present_value / 10000) / 100 + ' M$'
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  {selectedSolution !== null && !isEmpty(selectedSolution) ? (
                    <>
                      <Box>
                        <Typography color="text.primary" variant="h5" sx={{ mt: 2, mb: 2 }}>
                          Plant Cleaning Time
                        </Typography>
                        <PieChart data={selectedSolution?.cleaning_time} title={''} />
                        <Typography color={'text.primary'} sx={{ textAlign: 'center' }}>
                          Total: {Math.round(selectedSolution?.time_to_clean * 100) / 100} days
                        </Typography>
                      </Box>
                      <Box>
                        <Typography color="text.primary" variant="h5" sx={{ mt: 2, mb: 2 }}>
                          Production
                        </Typography>
                        <Grid container size={12} sx={{ mb: 2 }} spacing={2}>
                          <SMTCard
                            size={4}
                            name={'Yearly Soiling Loss'}
                            value={Math.round(Number(selectedSolution?.data?.yearly?.values?.production_losses) * 100) / 100}
                            unit={'MWh'}
                            help={'yearlySoilingLoss'}
                          />
                          <SMTCard
                            size={4}
                            name={'Yearly Cleaning Gains'}
                            value={Math.round(Number(selectedSolution?.data?.yearly?.values?.cleaning_gains) * 100) / 100}
                            unit={'MWh'}
                            help={'yearlyCleaningGains'}
                          />
                        </Grid>
                        <ProductionChart
                          reference={data?.data?.daily}
                          solutions={[
                            {
                              name: selectedSolutions?.[0],
                              ...selectedSolution,
                            },
                          ]}
                        />
                      </Box>
                      <Box sx={{ width: '100%' }}>
                        <Typography color="text.primary" variant="h5" sx={{ mt: 2, mb: 2 }}>
                          Financial
                          <Tooltip title={copiedText ? 'Table copied!' : 'Copy table'}>
                            <IconButton
                              onClick={() => {
                                handleFinancialTextCopy(selectedSolution?.cashflow?.cleaning_gains?.map((_item, index) => index) ?? []);
                              }}
                            >
                              {copiedText ? (
                                <CheckCircleIcon fontSize="small" sx={{ color: '#008000' }} />
                              ) : (
                                <ContentCopyRoundedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          {selectedSolution?.cashflow?.details && (
                            <DownloadCashflowButton
                              cashflow={selectedSolution.cashflow}
                              fileName={`cashflow_${getSolutionDisplayName(cleaningPackages, selectedSolutions?.[0])}.csv`}
                            />
                          )}
                        </Typography>
                        <Box sx={{ flexGrow: 1 }}>
                          <Grid container spacing={2}>
                            <Grid size={6}>
                              <TableContainer sx={{ maxHeight: 840 }} component={Paper}>
                                <Table stickyHeader size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Year</TableCell>
                                      <TableCell align="right">Cleaning Gains ($)</TableCell>
                                      <TableCell align="right">CAPEX ($)</TableCell>
                                      <TableCell align="right">OPEX ($)</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {selectedSolution?.cashflow?.cleaning_gains
                                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                      .map((item: number, index: number) => {
                                        const idx = page * rowsPerPage + index;
                                        return (
                                          <TableRow>
                                            <TableCell>Year {selectedSolution?.cashflow?.years[idx]}</TableCell>
                                            <TableCell align="right">{Math.round(item * 100) / 100}</TableCell>
                                            <TableCell align="right">
                                              {Math.round(Number(selectedSolution?.cashflow?.capex[idx]) * 100) / 100}
                                            </TableCell>
                                            <TableCell align="right">
                                              {Math.round(Number(selectedSolution?.cashflow?.opex[idx]) * 100) / 100}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              <TablePagination
                                rowsPerPageOptions={[10, 30, 50, 100]}
                                component="div"
                                count={selectedSolution?.cashflow?.cleaning_gains?.length ?? 0}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                              />
                            </Grid>
                            <Grid size={6}>
                              <Grid container spacing={2}>
                                <Grid size={12} justifyContent="center" alignItems="center">
                                  <InfoAlert severity="info">
                                    The pie charts below show the distribution of total CAPEX and OPEX across the entire optimization
                                    horizon, actualized to today's value using the discount rate.
                                  </InfoAlert>
                                </Grid>
                                <Grid size={12} justifyContent="center" alignItems="center">
                                  <PieChart data={selectedSolution?.capex} title={'Cleaning CAPEX ($)'} unit={'$'} />
                                  <Typography color={'text.primary'} sx={{ textAlign: 'center' }}>
                                    Total: {Math.round(selectedSolution?.capex?.total * 100) / 100} $
                                  </Typography>
                                </Grid>
                                <Grid size={12} justifyContent="center" alignItems="center">
                                  <PieChart data={selectedSolution?.opex} title={'Cleaning OPEX ($)'} unit={'$'} />
                                  <Typography color={'text.primary'} sx={{ textAlign: 'center' }}>
                                    Total: {Math.round(selectedSolution?.opex?.total * 100) / 100} $
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                      <Box>
                        <Typography color="text.primary" variant="h5" sx={{ mt: 2, mb: 2 }}>
                          Soiling Ratio
                        </Typography>
                        <Grid container size={12} sx={{ mb: 2 }}>
                          <SMTCard
                            size={4}
                            name={'Yearly Soiling Level'}
                            value={Math.round((1 - Number(selectedSolution?.data?.yearly?.values?.soiling_ratio)) * 10000) / 100}
                            unit={'%'}
                          />
                        </Grid>

                        <SoilingRatioChart
                          rain_dates={data?.rain_dates}
                          reference={data?.data?.daily}
                          solutions={
                            selectedSolutions?.[0] && data?.package_solution_groups?.[selectedSolutions?.[0]]
                              ? [
                                  {
                                    name: selectedSolutions?.[0],
                                    ...selectedSolution,
                                  },
                                ]
                              : []
                          }
                          ymin={0.4}
                          ymax={1.2}
                        />
                      </Box>
                      <Box>
                        <Typography color="text.primary" variant="h6" sx={{ mt: 2, mb: 2 }}>
                          Monthly Average Soiling Ratio
                        </Typography>
                        <Card sx={{ mt: 2 }}>
                          <CardContent>
                            <Typography variant="h5" noWrap color="text.primary" sx={{ mt: 1 }}>
                              Monthly Average Soiling Ratio
                              <Tooltip title={copiedText ? 'Table copied!' : 'Copy table'}>
                                <IconButton
                                  onClick={() => {
                                    handleTextCopy(selectedSolution?.data.monthly?.times);
                                  }}
                                >
                                  {copiedText ? (
                                    <CheckCircleIcon fontSize="small" sx={{ color: '#008000' }} />
                                  ) : (
                                    <ContentCopyRoundedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Typography>
                            <TableContainer component={Paper} sx={{ mt: 1 }}>
                              <Table sx={{ minWidth: 650 }} size="small" aria-label="simple table">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Month</TableCell>
                                    <TableCell align="right">Soiling Ratio (%)</TableCell>
                                    <TableCell align="right">Soiling Level (%)</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {selectedSolution?.data?.monthly?.times?.map((t: string, index: number) => {
                                    const d = new Date(Date.parse(t));
                                    const ts = d.toLocaleString(locale, { month: 'long' });
                                    const soiling_ratio =
                                      Math.round(selectedSolution?.data?.monthly?.values?.soiling_ratio[index] * 10000) / 100;
                                    const soiling_level =
                                      Math.round((1 - selectedSolution?.data?.monthly?.values?.soiling_ratio[index]) * 10000) / 100;
                                    return (
                                      <TableRow key={t} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell component="th" scope="row">
                                          {ts}
                                        </TableCell>
                                        <TableCell align="right">{soiling_ratio}</TableCell>
                                        <TableCell align="right">{soiling_level}</TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </CardContent>
                        </Card>
                      </Box>
                    </>
                  ) : (
                    <Typography>This combination doesn't exist. </Typography>
                  )}
                </Stack>
              </>
            ) : null}
            {selectedSolutions.length > 1 ? (
              <>
                <FinancialComparison
                  data={selectedSolutions?.map((name) => {
                    return {
                      name: name,
                      ...data?.package_solution_groups[name].solutions[data?.package_solution_groups[name].optimal_index],
                    };
                  })}
                />
                <ProductionChart
                  reference={data?.data?.daily}
                  solutions={selectedSolutions?.map((name) => {
                    return {
                      name: name,
                      ...data?.package_solution_groups[name].solutions[data?.package_solution_groups[name].optimal_index],
                    };
                  })}
                />
                <CompareProductionLosses
                  data={selectedSolutions?.map((name) => {
                    return {
                      name: name,
                      ...data?.package_solution_groups[name].solutions[data?.package_solution_groups[name].optimal_index],
                    };
                  })}
                />
                <CompareCleaningSchedules
                  rain_dates={data?.rain_dates}
                  data={selectedSolutions?.map((name) => {
                    return {
                      name: name,
                      ...data?.package_solution_groups[name].solutions[data?.package_solution_groups[name].optimal_index],
                    };
                  })}
                />
                <SoilingRatioChart
                  rain_dates={data?.rain_dates}
                  reference={data?.data?.daily}
                  solutions={selectedSolutions?.map((name) => {
                    return {
                      name: name,
                      ...data?.package_solution_groups[name].solutions[data?.package_solution_groups[name].optimal_index],
                    };
                  })}
                />
              </>
            ) : null}
          </>
        ) : null}
      </Box>
    </>
  );
};

export default AppSoilingMitigationStrategy;
